<template>
    <div class="quantity-input">
        <button
            class="circle-button decrease"
            ref="decreaseButton"
            @click="decreaseValue"
            :disabled="isDecreaseDisabled"
        >
            <i class="fal fa-minus"></i>
        </button>

        <div class="value">{{ currentValue }}</div>

        <button
            class="circle-button increase"
            ref="increaseButton"
            @click="increaseValue"
            :disabled="isIncreaseDisabled"
        >
            <i class="fal fa-plus"></i>
        </button>
    </div>
</template>

<script setup lang="ts">
    import { Ripple } from "@/shared/ui/Ripple";
    import { computed, onMounted, ref, watch } from "vue";

    // define properties
    const props = defineProps<{
        min: number;
        max: number;
        quantity: number;
        disabled: boolean;
    }>();

    // define emmitted events
    const emit = defineEmits(["update:quantity"]);

    // define reactive variables
    const currentValue = ref(props.quantity);

    // define methods
    function decreaseValue() {
        setCurrentValue(currentValue.value - 1);
    }

    function increaseValue() {
        setCurrentValue(currentValue.value + 1);
    }

    function setCurrentValue(val: number) {
        currentValue.value = val;
        emit("update:quantity", currentValue.value);
    }

    function fixCurrentValue() {
        let va = currentValue.value;
        if (props.min !== undefined && va < props.min) {
            va = props.min;
        }

        if (props.max !== undefined && va > props.max) {
            va = props.max;
        }
        setCurrentValue(va);
    }

    // define computed
    const isDecreaseDisabled = computed(
        () => props.disabled || (props.min !== undefined && currentValue.value <= props.min)
    );
    const isIncreaseDisabled = computed(
        () => props.disabled || (props.max !== undefined && currentValue.value >= props.max)
    );

    // define html refs
    const decreaseButton = ref<HTMLElement>();
    const increaseButton = ref<HTMLElement>();

    // define watchers
    watch(
        () => props.quantity,
        (newValue) => {
            currentValue.value = newValue;
        }
    );
    watch(() => props.min, fixCurrentValue);
    watch(() => props.max, fixCurrentValue);

    // define onMounted actions
    onMounted(() => {
        fixCurrentValue();

        Ripple.bind($(decreaseButton.value as any));
        Ripple.bind($(increaseButton.value as any));
    });
</script>

<style lang="scss">
    @use "./../../../assets/styles/scss/shared/_colors.scss";

    .quantity-input {
        width: 128px;
        height: 38px;
        position: relative;

        > .circle-button {
            position: absolute;
            top: 0;
            width: 38px;
            height: 38px;
            border-radius: 50%;
            background-color: colors.$fontColor;
            text-align: center;

            &:disabled {
                background-color: colors.$lightFontColor !important;
            }

            &:active {
                background-color: colors.$fontColorHover;
            }

            > .fal {
                color: #fff;
                font-size: 14px;
                line-height: 38px;
            }

            &.decrease {
                left: 0;
            }

            &.increase {
                right: 0;
            }
        }

        > .value {
            padding: 0 38px;
            text-align: center;
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
            line-height: 38px;
            font-size: 18px;
            color: colors.$fontColor;
            font-weight: 400;
        }
    }
</style>
